import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTrip } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PostTrip = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    origin_address: '',
    destination_address: '',
    departure_time: '',
    seats_available: 1,
    price_suggestion: 0,
    price_negotiable: true,
    notes: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else if (!user.is_verified) {
      navigate('/membership');
    }
  }, [user, navigate]);

  if (!user || !user.is_verified) return null;

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await createTrip(formData);
      navigate(`/trips/${res.data.trip.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post trip. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>Offer a Ride</h2>
        <p style={styles.subtitle}>
          Share your trip and help someone get where they're going.
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Starting From</label>
            <input
              style={styles.input}
              type="text"
              name="origin_address"
              value={formData.origin_address}
              onChange={handleChange}
              placeholder="Providence, Rhode Island"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Going To</label>
            <input
              style={styles.input}
              type="text"
              name="destination_address"
              value={formData.destination_address}
              onChange={handleChange}
              placeholder="New York City, New York"
              required
            />
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Departure Date & Time</label>
              <input
                style={styles.input}
                type="datetime-local"
                name="departure_time"
                value={formData.departure_time}
                onChange={handleChange}
                required
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Seats Available</label>
              <input
                style={styles.input}
                type="number"
                name="seats_available"
                value={formData.seats_available}
                onChange={handleChange}
                min="1"
                max="8"
                required
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Suggested Contribution ($)</label>
              <input
                style={styles.input}
                type="number"
                name="price_suggestion"
                value={formData.price_suggestion}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="0 for free"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Price Negotiable?</label>
              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  name="price_negotiable"
                  checked={formData.price_negotiable}
                  onChange={handleChange}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxLabel}>Yes, open to offers</span>
              </div>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Notes for Riders</label>
            <textarea
              style={styles.textarea}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="e.g. Comfortable SUV, stopping once for gas, no smoking, dog friendly..."
              rows="3"
            />
          </div>

          <div style={styles.notice}>
            By posting this trip you confirm you are a verified Sytch member
            and agree to our terms of service.
          </div>

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Posting...' : 'Post Trip'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'Arial, sans-serif',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '48px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
    color: '#1a1a2e',
  },
  subtitle: {
    color: '#666',
    margin: '0 0 32px 0',
  },
  error: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  field: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 0',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '15px',
    color: '#333',
  },
  notice: {
    background: '#e3f2fd',
    color: '#1565c0',
    padding: '12px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  btn: {
    width: '100%',
    padding: '14px',
    background: '#4fc3f7',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
};

export default PostTrip;